import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { Seo } from '@/components/Seo'
import { Button } from '@/components/ui/Button'
import { apiErrorMessage, isApiUnreachable } from '@/lib/api/errors'
import { useT, LocaleLink } from '@/i18n'
import { NotFoundPage } from './NotFoundPage'

/**
 * Route-level `errorElement`. The router renders this when a route throws while
 * loading or rendering (and for unmatched routes that produce a 404 response).
 *
 * Crucially it distinguishes the two cases:
 *  - A genuine **404** (route response with status 404) → the gothic
 *    "wandered beyond the map" {@link NotFoundPage}.
 *  - Any **unexpected render crash** → a themed, recoverable error screen with a
 *    Reload action, NOT the 404 copy. A failed data fetch should never reach
 *    here (sections handle their own errors inline); this is the last-resort net
 *    for a real crash, and it stays in the horror-fantasy voice instead of
 *    masquerading as a missing page.
 */
export function RouteErrorBoundary() {
  const t = useT()
  const error = useRouteError()

  // Unmatched route / explicit 404 → the dedicated NotFound screen.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />
  }

  const message = isApiUnreachable(error)
    ? apiErrorMessage(error)
    : error instanceof Error && error.message
      ? error.message
      : isRouteErrorResponse(error)
        ? error.statusText || t('system.error.unknownDisturbance')
        : t('system.error.unknownDisturbance')

  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 px-4 py-24 text-center">
      <Seo title={t('system.error.seoTitle')} noIndex />
      <p className="font-gothic text-7xl text-blood-400">⚠</p>
      <h1 className="title-gothic text-2xl">{t('system.error.heading')}</h1>
      <p className="font-body text-bone-dim">{t('system.error.body')}</p>
      <p className="surface-parchment max-w-lg px-4 py-2 font-body text-xs text-bone-dim">{message}</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button variant="blood" onClick={() => window.location.reload()}>
          {t('system.error.tryAgain')}
        </Button>
        <LocaleLink to="/">
          <Button variant="metal">{t('system.error.returnCta')}</Button>
        </LocaleLink>
      </div>
    </div>
  )
}

export default RouteErrorBoundary
